import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { splitSiteName } from "@/lib/siteName";

export default function Footer() {
  const { t } = useTranslation();
  const { site, contactDetails, contactDisplay } = useSiteSettings();
  const year = new Date().getFullYear();
  const brand = splitSiteName(site.site_name);

  const links = [
    { label: t("nav.home"), to: "/" },
    { label: t("nav.engines"), to: "/engines" },
    { label: t("nav.transmissions"), to: "/transmissions" },
    { label: t("nav.bodyParts"), to: "/body-parts" },
    { label: t("nav.contact"), to: "/contact-us" },
  ];

  const companyLinks = [
    { label: "Testimonials", to: "/testimonials" },
    { label: "Return Policy", to: "/return-policy" },
    { label: "Terms & Conditions", to: "/terms-and-conditions" },
  ];

  const phones = contactDetails.filter((c) => c.type === "phone");
  const emails = contactDetails.filter((c) => c.type === "email");
  const addresses = contactDetails.filter((c) => c.type === "address");

  return (
    <footer className="w-full bg-brand-dark2 text-white">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-gold text-brand-dark">
                <i className="ri-car-line text-2xl"></i>
              </span>
              {brand.second ? (
                <span className="leading-tight">
                  <span className="font-heading text-lg font-semibold uppercase block">
                    {brand.first}
                  </span>
                  <span className="font-heading text-[11px] tracking-[0.25em] uppercase text-brand-gold block">
                    {brand.second}
                  </span>
                </span>
              ) : (
                <span className="font-heading text-lg font-semibold uppercase">
                  {brand.first}
                </span>
              )}
            </Link>
            <p className="mt-4 text-sm text-white/60 leading-relaxed">
              {site.footer_text || t("footer.brandDesc")}
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-widest text-brand-gold">
              {t("footer.quickLinks")}
            </h4>
            <ul className="mt-4 space-y-2">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-white/60 hover:text-brand-gold transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-widest text-brand-gold">
              {t("footer.company")}
            </h4>
            <ul className="mt-4 space-y-2">
              {companyLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-white/60 hover:text-brand-gold transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-widest text-brand-gold">
              {t("footer.getInTouch")}
            </h4>
            <ul className="mt-4 space-y-3 text-sm text-white/60">
              {contactDisplay.address_footer && addresses.map((a) => (
                <li key={a.id} className="flex items-start gap-3">
                  <i className="ri-map-pin-line mt-0.5 text-brand-gold"></i>
                  <span>{a.value}</span>
                </li>
              ))}
              {contactDisplay.phone_footer && phones.map((p) => (
                <li key={p.id} className="flex items-center gap-3">
                  <i className="ri-phone-line text-brand-gold"></i>
                  <a href={`tel:${p.value.replace(/\s/g, "")}`} className="hover:text-brand-gold transition-colors">
                    {p.value}
                  </a>
                </li>
              ))}
              {contactDisplay.email_footer && emails.map((em) => (
                <li key={em.id} className="flex items-center gap-3">
                  <i className="ri-mail-line text-brand-gold"></i>
                  <a href={`mailto:${em.value}`} className="hover:text-brand-gold transition-colors">
                    {em.value}
                  </a>
                </li>
              ))}
              <li className="flex items-center gap-3">
                <i className="ri-time-line text-brand-gold"></i>
                <span>{t("footer.monFri")}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/40">
            © {year} {site.site_name}. All Rights Reserved.
          </p>
          <p className="text-xs text-white/40">
            {t("footer.disclaimer", { siteName: site.site_name })}
          </p>
        </div>
      </div>
    </footer>
  );
}